<template>
  <div dir="rtl" class="mr-8 px-4 sm:px-6 lg:px-8">
    <div class="sm:flex sm:items-center">
      <div class="sm:flex-auto">
        <h1 class="text-xl font-semibold text-gray-900">رسائل حفظة</h1>
        <p class="mt-2 text-sm text-gray-700">إدارة الرسائل التي تظهر للمعلمين في لوحة التحكم</p>
      </div>
      <div class="mt-4 sm:mt-0 sm:mr-0">
        <button @click="showCreateModal = true" class="button">
          إضافة رسالة جديدة
        </button>
      </div>
    </div>

    <div class="mt-8">
      <div class="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div v-for="message in messages" :key="message.id" class="bg-white overflow-hidden shadow rounded-lg">
          <div class="p-6">
            <div class="flex items-center justify-between mb-4">
              <h3 class="text-lg font-medium text-gray-900">{{ message.title }}</h3>
              <div class="flex space-x-2 space-x-reverse">
                <button @click="toggleMessageStatus(message.id)" 
                        :class="['px-3 py-1 text-xs font-medium rounded-full', 
                                message.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800']">
                  {{ message.isActive ? 'مفعل' : 'معطل' }}
                </button>
                <button @click="editMessage(message)" class="btn-edit">تعديل</button>
                <button @click="deleteMessage(message.id)" class="btn-delete">حذف</button>
              </div>
            </div>
            <p class="text-gray-600 mb-4">{{ message.content }}</p>
            <div class="flex items-center justify-between text-sm text-gray-500">
              <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                {{ getMessageTypeText(message.messageType) }}
              </span>
              <span>الأولوية: {{ message.priority }}</span>
              <span>{{ formatDate(message.createdAt) }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Create/Edit Modal -->
    <div v-if="showCreateModal || showEditModal" class="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div class="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
        <div class="mt-3">
          <div class="flex items-center justify-between mb-4">
            <h3 class="text-lg font-medium text-gray-900">{{ showEditModal ? 'تعديل الرسالة' : 'إضافة رسالة جديدة' }}</h3>
            <button @click="closeModal" class="text-gray-400 hover:text-gray-600">
              <svg class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <form @submit.prevent="saveMessage" class="space-y-4">
            <div>
              <label class="block text-sm font-medium text-gray-700">العنوان</label>
              <input v-model="formData.title" type="text" required 
                     class="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" />
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700">المحتوى</label>
              <textarea v-model="formData.content" rows="4" 
                        class="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"></textarea>
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700">نوع الرسالة</label>
              <select v-model="formData.messageType" 
                      class="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md">
                <option value="WELCOME">رسالة ترحيب</option>
                <option value="ANNOUNCEMENT">إعلان</option>
                <option value="MAINTENANCE">صيانة</option>
                <option value="GENERAL">عام</option>
              </select>
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700">الأولوية</label>
              <input v-model="formData.priority" type="number" min="1" max="10" 
                     class="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" />
            </div>
            <div class="flex items-center">
              <input v-model="formData.isActive" type="checkbox" 
                     class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" />
              <label class="mr-2 block text-sm text-gray-900">مفعل</label>
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700">تاريخ البداية (اختياري)</label>
              <input v-model="formData.startDate" type="datetime-local" 
                     class="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" />
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700">تاريخ النهاية (اختياري)</label>
              <input v-model="formData.endDate" type="datetime-local" 
                     class="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" />
            </div>
            <div class="flex justify-end space-x-3 space-x-reverse mt-6">
              <button type="button" @click="closeModal" class="btn-secondary">إلغاء</button>
              <button type="submit" class="button">
                {{ showEditModal ? 'تحديث' : 'إضافة' }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue';
import { getAllMessages, createMessage, updateMessage, deleteMessage as deleteMessageService, toggleMessageStatus as toggleMessageStatusService } from '../services/adminMessages';

export default {
  name: 'AdminMessages',
  setup() {
    const messages = ref([]);
    const showCreateModal = ref(false);
    const showEditModal = ref(false);
    const editingMessage = ref(null);
    const formData = ref({
      title: '',
      content: '',
      messageType: 'GENERAL',
      priority: 1,
      isActive: true,
      startDate: '',
      endDate: ''
    });

    const loadMessages = async () => {
      try {
        const data = await getAllMessages();
        console.log('Loaded messages:', data);
        messages.value = data;
      } catch (error) {
        console.error('Error loading messages:', error);
        // Show user-friendly error message
        alert('حدث خطأ أثناء تحميل الرسائل. يرجى المحاولة مرة أخرى.');
      }
    };

    const saveMessage = async () => {
      try {
        if (showEditModal.value) {
          await updateMessage(editingMessage.value.id, formData.value);
        } else {
          await createMessage(formData.value);
        }
        await loadMessages();
        closeModal();
        // Show success message
        alert(showEditModal.value ? 'تم تحديث الرسالة بنجاح' : 'تم إضافة الرسالة بنجاح');
      } catch (error) {
        console.error('Error saving message:', error);
        // Show user-friendly error message
        alert('حدث خطأ أثناء حفظ الرسالة. يرجى المحاولة مرة أخرى.');
      }
    };

    const editMessage = (message) => {
      editingMessage.value = message;
      // Convert date format from "YYYY-MM-DD HH:mm:ss" to "YYYY-MM-DDTHH:mm" for datetime-local input
      const formatDateForInput = (dateString) => {
        if (!dateString) return '';
        // Convert "2025-07-20 19:26:00" to "2025-07-20T19:26"
        return dateString.replace(' ', 'T').substring(0, 16);
      };
      
      formData.value = {
        ...message,
        startDate: formatDateForInput(message.startDate),
        endDate: formatDateForInput(message.endDate),
      };
      showEditModal.value = true;
    };

    const deleteMessage = async (messageId) => {
      if (confirm('هل أنت متأكد من حذف هذه الرسالة؟')) {
        try {
          await deleteMessageService(messageId);
          await loadMessages();
          // Show success message
          alert('تم حذف الرسالة بنجاح');
        } catch (error) {
          console.error('Error deleting message:', error);
          // Show user-friendly error message
          alert('حدث خطأ أثناء حذف الرسالة. يرجى المحاولة مرة أخرى.');
        }
      }
    };

    const toggleMessageStatus = async (messageId) => {
      try {
        await toggleMessageStatusService(messageId);
        await loadMessages();
        // Show success message
        alert('تم تغيير حالة الرسالة بنجاح');
      } catch (error) {
        console.error('Error toggling message status:', error);
        // Show user-friendly error message
        alert('حدث خطأ أثناء تغيير حالة الرسالة. يرجى المحاولة مرة أخرى.');
      }
    };

    const closeModal = () => {
      showCreateModal.value = false;
      showEditModal.value = false;
      editingMessage.value = null;
      formData.value = {
        title: '',
        content: '',
        messageType: 'GENERAL',
        priority: 1,
        isActive: true,
        startDate: '',
        endDate: ''
      };
    };

    const getMessageTypeText = (type) => {
      const types = {
        WELCOME: 'رسالة ترحيب',
        ANNOUNCEMENT: 'إعلان',
        MAINTENANCE: 'صيانة',
        GENERAL: 'عام'
      };
      return types[type] || type;
    };

    const formatDate = (dateString) => {
      if (!dateString) return 'غير محدد';
      
      try {
        // Handle different date formats that might come from the backend
        let date;
        if (typeof dateString === 'string') {
          // If it's already a string, try to parse it
          date = new Date(dateString);
        } else if (dateString instanceof Date) {
          // If it's already a Date object
          date = dateString;
        } else {
          // If it's null or undefined
          return 'غير محدد';
        }
        
        // Check if the date is valid
        if (isNaN(date.getTime())) {
          return 'غير محدد';
        }
        
        // Format the date in Arabic locale
        return date.toLocaleDateString('ar-SA', {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        });
      } catch (error) {
        console.error('Error formatting date:', error, dateString);
        return 'غير محدد';
      }
    };

    onMounted(() => {
      loadMessages();
    });

    return {
      messages,
      showCreateModal,
      showEditModal,
      formData,
      saveMessage,
      editMessage,
      deleteMessage,
      toggleMessageStatus,
      closeModal,
      getMessageTypeText,
      formatDate
    };
  }
};
</script>

<style scoped>
.button {
  @apply px-4 py-2 bg-customHafadhColor text-white rounded hover:bg-customHadhColorHover w-40 h-10;
}

.btn-secondary {
  @apply px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600 w-20 h-10;
}

.btn-edit {
  @apply px-3 py-1 bg-green-600 text-white text-sm rounded hover:bg-green-700;
}

.btn-delete {
  @apply px-3 py-1 bg-red-600 text-white text-sm rounded hover:bg-red-700;
}
</style> 